import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import type { Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { type Locale, translateLabel } from "../i18n/translate";
import { AddButton } from "../design/AddLink";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// Sweep §4 / 0221–0223. THE H&S RISK ASSESSMENT, per event and per post.
//
// Every score, band, label and tone arrives from the database — this screen
// multiplies nothing. A signed version cannot be changed; saving against it
// starts the next version. Signing off puts the controls into the briefing of
// every post it covers, and the crew must read them again before they work.
// ---------------------------------------------------------------------------

type Hazard = {
  seq: number;
  hazard: string;
  who_at_risk: string;
  likelihood: number;
  likelihood_label: string;
  severity: number;
  severity_label: string;
  inherent_score: number;
  inherent_band_label: string;
  inherent_band_tone: Tone;
  controls: string;
  residual_likelihood: number;
  residual_likelihood_label: string;
  residual_severity: number;
  residual_severity_label: string;
  residual_score: number;
  residual_band_label: string;
  residual_band_tone: Tone;
  residual_may_sign_off: boolean;
};

type Version = {
  id: string;
  requirement_id: string | null;
  version_no: number;
  state_label: string;
  state_tone: Tone;
  assessed_by_you: boolean;
  assessed_at: string;
  signed_off_by_you: boolean | null;
  signed_off_at: string | null;
  signable: boolean;
  hazards: Hazard[];
};

type Rank = { rank: number; label: string; description: string };
type Cell = { likelihood: number; severity: number; score: number; band_label: string; band_tone: Tone };
type Post = { id: string; label: string; zone?: string | null; role_name?: string };

// The API joins "zone · role" in English; the role is a word the reader's language may have.
function postLabel(locale: Locale, p: Post): string {
  return p.role_name ? [p.zone, translateLabel(locale, p.role_name)].filter(Boolean).join(" · ") : p.label;
}

type Data = {
  assessments: Version[];
  likelihoods: Rank[];
  severities: Rank[];
  matrix: Cell[];
  posts: Post[];
  may_manage: boolean;
  may_sign_off: boolean;
};

const EVENT_WIDE = "";

export function RiskAssessments() {
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const eventId = useParams()["id"] ?? "";
  const [drafting, setDrafting] = useState(false);
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<Data> & { error?: { message?: string } }>(console_.eventRiskAssessments(apiBase, eventId));
      if (res.status === 200 && Array.isArray(res.body.assessments)) {
        setData(res.body as Data);
        setFailed(null);
      } else {
        setData(null);
        setFailed(refusal(res.body.error) ?? t("The risk assessment for this event could not be read."));
      }
    } catch {
      setData(null);
      setFailed(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // One group per scope, newest version first (the API's order).
  const scopes: { key: string; label: string; versions: Version[] }[] = [];
  for (const v of data?.assessments ?? []) {
    const key = v.requirement_id ?? EVENT_WIDE;
    let group = scopes.find((s) => s.key === key);
    if (!group) {
      const post = data?.posts.find((p) => p.id === key);
      const label = v.requirement_id === null ? t("The whole event") : (post ? postLabel(locale, post) : t("A post"));
      group = { key, label, versions: [] };
      scopes.push(group);
    }
    group.versions.push(v);
  }

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("Risk assessment")}</h1>
      </div>
      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not available")} />
          <span>{failed}</span>
        </div>
      ) : null}
      {data ? (
        <Tabs
          label={t("Risk assessment")}
          tabs={[
            {
              id: "assessment",
              label: t("Assessment"),
              content: (
                <div className="stack">
      {data && scopes.length === 0 ? (
        <section className="panel">
          <p className="muted">{t("No risk assessment has been written for this event.")}</p>
        </section>
      ) : null}
      {scopes.map((s) => (
        <section className="panel stack" key={s.key || "event"}>
          <h2>{s.label}</h2>
          <VersionView version={s.versions[0]!} orgZone={orgZone} maySignOff={data?.may_sign_off ?? false} onChanged={load} />
          {s.versions.length > 1 ? (
            <details>
              <summary>{t("Earlier versions ({n})", { n: s.versions.length - 1 })}</summary>
              {s.versions.slice(1).map((v) => (
                <VersionView key={v.id} version={v} orgZone={orgZone} maySignOff={false} onChanged={load} />
              ))}
            </details>
          ) : null}
        </section>
      ))}
      {data?.may_manage ? (
        drafting ? (
          <DraftForm
            data={data}
            eventId={eventId}
            onSaved={async () => {
              await load();
              setDrafting(false);
            }}
            onCancel={() => setDrafting(false)}
          />
        ) : (
          <div className="panel-head">
            <AddButton label={t("Write or change an assessment")} expanded={false} onClick={() => setDrafting(true)} />
          </div>
        )
      ) : null}
                </div>
              ),
            },
            { id: "scoring", label: t("Scoring"), content: <Matrix data={data} /> },
          ]}
        />
      ) : null}
    </div>
  );
}

/** The scale in words and colour: likelihood down the side, severity across the top. */
function Matrix({ data }: { data: Data }) {
  const { locale, t } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const rows = [...data.likelihoods].reverse();
  const bands = [...new Map(data.matrix.map((c) => [c.band_label, { label: c.band_label, tone: c.band_tone, score: c.score }])).values()].sort((a, b) => a.score - b.score);
  return (
    <section className="panel stack">
      <h2>{t("How risk is scored")}</h2>
      <p className="muted">{t("Each cell is the score; the colour and the list below give the band it falls in.")}</p>
      <div>
        <table className="data-table">
          <caption className="visually-hidden">{t("Risk matrix: likelihood by severity")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Likelihood ↓ · Severity →")}</th>
              {data.severities.map((s) => (
                <th scope="col" key={s.rank} title={tl(s.description)}>
                  {s.rank} · {tl(s.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.rank}>
                <th scope="row" title={l.description}>
                  {l.rank} · {tl(l.label)}
                </th>
                {data.severities.map((s) => {
                  const cell = data.matrix.find((c) => c.likelihood === l.rank && c.severity === s.rank);
                  return (
                    <td key={s.rank}>
                      {cell ? <StatusChip tone={cell.band_tone} label={String(cell.score)} /> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="plain-list row" aria-label={t("Bands")}>
        {bands.map((b) => (
          <li key={b.label}>
            <StatusChip tone={b.tone} label={tl(b.label)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function VersionView({
  version: v,
  orgZone,
  maySignOff,
  onChanged,
}: {
  version: Version;
  orgZone: string | null;
  maySignOff: boolean;
  onChanged: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const blocked = v.hazards.some((h) => !h.residual_may_sign_off);

  async function signOff() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ risk_assessment?: { briefings_published?: number }; error?: { message?: string } }>(
        console_.signOffRiskAssessment(apiBase, v.id),
      );
      if (res.status !== 200) {
        setNotice({ tone: "attention", text: refusal(res.body.error) ?? t("That risk assessment could not be signed off.") });
        return;
      }
      const n = res.body.risk_assessment?.briefings_published ?? 0;
      setNotice({ tone: "live", text: t("Signed off. {n} post briefing(s) republished for the crew to read.", { n }) });
      await onChanged();
    } catch {
      setNotice({ tone: "attention", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <StatusChip tone={v.state_tone} label={tl(v.state_label)} />
        <strong>{t("Version {n}", { n: v.version_no })}</strong>
        <span className="muted">
          {t("Assessed by {who}, {when}", { who: v.assessed_by_you ? t("you") : t("a colleague"), when: whenIn(v.assessed_at, orgZone) })}
          {v.signed_off_at
            ? ` · ${t("signed off by {who}, {when}", { who: v.signed_off_by_you ? t("you") : t("a colleague"), when: whenIn(v.signed_off_at, orgZone) })}`
            : ""}
        </span>
      </div>
      <div>
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Hazard")}</th>
              <th scope="col">{t("Who is at risk")}</th>
              <th scope="col">{t("Before controls")}</th>
              <th scope="col">{t("Controls")}</th>
              <th scope="col">{t("After controls")}</th>
            </tr>
          </thead>
          <tbody>
            {v.hazards.map((h) => (
              <tr key={h.seq}>
                <td>{h.hazard}</td>
                <td>{h.who_at_risk}</td>
                <td>
                  <StatusChip tone={h.inherent_band_tone} label={`${h.inherent_score} · ${tl(h.inherent_band_label)}`} />
                  <div className="muted">
                    {tl(h.likelihood_label)} × {tl(h.severity_label)}
                  </div>
                </td>
                <td className="pre-line">{h.controls}</td>
                <td>
                  <StatusChip tone={h.residual_band_tone} label={`${h.residual_score} · ${tl(h.residual_band_label)}`} />
                  <div className="muted">
                    {tl(h.residual_likelihood_label)} × {tl(h.residual_severity_label)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.signable && maySignOff ? (
        <div className="row">
          <button type="button" className="btn" disabled={busy || blocked} onClick={() => void signOff()}>
            {busy ? t("Signing off…") : t("Sign off")}
          </button>
          <span className="muted">
            {blocked
              ? t("A hazard is still too risky after its controls. Reduce it before signing off.")
              : t("Once signed it cannot be changed, and every booked worker must read the controls again before their shift.")}
          </span>
        </div>
      ) : null}
      {notice ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={notice.tone} label={notice.tone === "live" ? t("Done") : t("Not signed off")} />
          <span>{notice.text}</span>
        </div>
      ) : null}
    </div>
  );
}

type Row = {
  hazard: string;
  who_at_risk: string;
  likelihood: string;
  severity: string;
  controls: string;
  residual_likelihood: string;
  residual_severity: string;
};

const EMPTY: Row = {
  hazard: "",
  who_at_risk: "",
  likelihood: "",
  severity: "",
  controls: "",
  residual_likelihood: "",
  residual_severity: "",
};

function rowsFrom(v: Version | undefined): Row[] {
  if (!v) return [{ ...EMPTY }];
  return v.hazards.map((h) => ({
    hazard: h.hazard,
    who_at_risk: h.who_at_risk,
    likelihood: String(h.likelihood),
    severity: String(h.severity),
    controls: h.controls,
    residual_likelihood: String(h.residual_likelihood),
    residual_severity: String(h.residual_severity),
  }));
}

function DraftForm({
  data,
  eventId,
  onSaved,
  onCancel,
}: {
  data: Data;
  eventId: string;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [scope, setScope] = useState(EVENT_WIDE);
  const latest = data.assessments.find((v) => (v.requirement_id ?? EVENT_WIDE) === scope);
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(latest));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  function choose(next: string) {
    setScope(next);
    setRows(rowsFrom(data.assessments.find((v) => (v.requirement_id ?? EVENT_WIDE) === next)));
    setNotice(null);
  }

  function set(i: number, key: keyof Row, value: string) {
    setRows(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  }

  async function save() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        console_.saveRiskAssessment(apiBase, eventId, {
          requirement_id: scope === EVENT_WIDE ? null : scope,
          hazards: rows.map((r) => ({
            hazard: r.hazard,
            who_at_risk: r.who_at_risk,
            likelihood: Number(r.likelihood),
            severity: Number(r.severity),
            controls: r.controls,
            residual_likelihood: Number(r.residual_likelihood),
            residual_severity: Number(r.residual_severity),
          })),
        }),
      );
      if (res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("That risk assessment could not be saved."));
        return;
      }
      await onSaved();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const scale = (id: string, label: string, value: string, options: Rank[], onChange: (v: string) => void) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} required value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t("Choose…")}</option>
        {options.map((o) => (
          <option key={o.rank} value={String(o.rank)}>
            {o.rank} · {translateLabel(locale, o.label)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form
      className="panel stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h2>{t("Write or change an assessment")}</h2>
      <p className="muted">
        {t("Saving changes the draft. If the latest version is already signed off, saving starts a new version and the signed one is kept as it was. Describe who is at risk as a group — never a named person or anybody's health.")}
      </p>
      <div className="field">
        <label htmlFor="ra-scope">{t("Covers")}</label>
        <select id="ra-scope" value={scope} onChange={(e) => choose(e.target.value)}>
          <option value={EVENT_WIDE}>{t("The whole event")}</option>
          {data.posts.map((p) => (
            <option key={p.id} value={p.id}>
              {postLabel(locale, p)}
            </option>
          ))}
        </select>
      </div>
      {rows.map((r, i) => (
        <fieldset className="stack" key={i}>
          <legend>{t("Hazard {n}", { n: i + 1 })}</legend>
          <div className="field">
            <label htmlFor={`ra-hazard-${i}`}>{t("Hazard")}</label>
            <input id={`ra-hazard-${i}`} required maxLength={500} value={r.hazard} onChange={(e) => set(i, "hazard", e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`ra-who-${i}`}>{t("Who is at risk")}</label>
            <input id={`ra-who-${i}`} required maxLength={300} value={r.who_at_risk} onChange={(e) => set(i, "who_at_risk", e.target.value)} />
          </div>
          <div className="row">
            {scale(`ra-l-${i}`, t("Likelihood before controls"), r.likelihood, data.likelihoods, (v) => set(i, "likelihood", v))}
            {scale(`ra-s-${i}`, t("Severity before controls"), r.severity, data.severities, (v) => set(i, "severity", v))}
          </div>
          <div className="field">
            <label htmlFor={`ra-controls-${i}`}>{t("Controls")}</label>
            <textarea id={`ra-controls-${i}`} required maxLength={2000} rows={3} value={r.controls} onChange={(e) => set(i, "controls", e.target.value)} />
            <p className="field-hint">{t("What the crew must do. This is what workers on the post read in their briefing.")}</p>
          </div>
          <div className="row">
            {scale(`ra-rl-${i}`, t("Likelihood after controls"), r.residual_likelihood, data.likelihoods, (v) => set(i, "residual_likelihood", v))}
            {scale(`ra-rs-${i}`, t("Severity after controls"), r.residual_severity, data.severities, (v) => set(i, "residual_severity", v))}
          </div>
          {rows.length > 1 ? (
            <div className="row">
              <button type="button" className="btn btn-quiet" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                {t("Remove hazard {n}", { n: i + 1 })}
              </button>
            </div>
          ) : null}
        </fieldset>
      ))}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not saved")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn btn-quiet" onClick={() => setRows([...rows, { ...EMPTY }])}>
          {t("Add a hazard")}
        </button>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("Saving…") : t("Save draft")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}
